import {
  CompletionRequirement,
  ServiceType,
  Timing,
} from '@wix/ambassador-bookings-services-v2-service/types';
import { ServiceData, SlotService } from '../state/types';
import { mapToArray } from '..';
import { OfferedAsType } from '../mappers/serviceMapper/utils/payment.mapper';
import { FormService } from '../mappers/serviceMapper/service.mapper';

// bulkCreateBooking accepts at most this many bookings per request.
export const MAX_PARTICIPANTS_PER_BOOKING = 12;

const MULTI_PARTICIPANT_SERVICE_TYPES = new Set<ServiceType>([
  ServiceType.CLASS,
  ServiceType.COURSE,
]);

const getSlotServices = (serviceData?: ServiceData | null): SlotService[] =>
  serviceData?.slotServices
    ? mapToArray<SlotService>(serviceData.slotServices)
    : [];

/**
 * A service that can only be paid with a pricing plan (no one-time payment).
 * Note: this is stricter than `isOfferedAsPricingPlanOnly` in payment utils, which
 * also requires connected plans; here a plan-only service with no plans still counts.
 */
export const hasPricingPlanOnlyService = (
  serviceData?: ServiceData | null,
): boolean =>
  getSlotServices(serviceData).some(
    ({ service }) =>
      service.payment?.offeredAs?.length === 1 &&
      service.payment.offeredAs[0] === OfferedAsType.PRICING_PLAN,
  );

/**
 * An intake form the booker must complete before booking. A visitor cannot fill an
 * intake form on behalf of another participant, so such services are excluded from
 * the multi-participant flow.
 */
export const hasIntakeFormRequiredAtBooking = (
  service: FormService,
): boolean => {
  const policy = service.intakeFormPolicy;
  return (
    !!policy?.enabled &&
    !!policy.formId &&
    policy.timing === Timing.BEFORE_BOOKING &&
    policy.completionRequirement ===
      CompletionRequirement.REQUIRED_BEFORE_BOOKING
  );
};

/**
 * Whether the "Add another participant" flow (one booking per participant, created in
 * bulk) is available for the loaded service.
 *
 * Group booking (policy `maxParticipantsPerBooking > 1`) keeps today's single booking
 * with N participants; the multi-participant flow only kicks in when the policy limits a
 * booking to one participant and allows booking for someone else.
 *
 * `slotService.service.maxNumberOfParticipantsWithoutPP` is pre-derived by the service
 * mapper as `min(policy.maxParticipantsPerBooking, availableSpots)`; together with the
 * `availableSpots >= 2` condition below it is <= 1 exactly when the policy is <= 1, so
 * it stands in for the raw policy value (the participants dropdown is hidden in the
 * same case). A service with no participants policy maps to 0 and is eligible on
 * purpose: no policy means no group booking, i.e. one participant per booking.
 */
export const getIsMultiParticipantFlowActive = (
  serviceData: ServiceData | null | undefined,
  isFeatureEnabled: boolean,
): boolean => {
  if (!isFeatureEnabled || !serviceData?.isSingleService) {
    return false;
  }
  const [slotService] = getSlotServices(serviceData);
  if (!slotService) {
    return false;
  }
  const { service } = slotService;

  return (
    MULTI_PARTICIPANT_SERVICE_TYPES.has(service.type) &&
    (service.maxNumberOfParticipantsWithoutPP ?? 1) <= 1 &&
    serviceData.allowBookForSomeoneElse &&
    !hasPricingPlanOnlyService(serviceData) &&
    !hasIntakeFormRequiredAtBooking(service) &&
    (service.availableSpots ?? 0) >= 2
  );
};

/**
 * Upper bound on participants in the multi-participant flow: never more than the
 * session's open spots, and never more than one bulk request can create.
 */
export const getMaxParticipants = (
  serviceData?: ServiceData | null,
): number => {
  const [slotService] = getSlotServices(serviceData);
  const availableSpots = slotService?.service.availableSpots ?? 1;
  return Math.max(1, Math.min(availableSpots, MAX_PARTICIPANTS_PER_BOOKING));
};

export const getMaxParticipantsReachedTooltipKey = (
  serviceData?: ServiceData | null,
): string => {
  const [slotService] = getSlotServices(serviceData);
  const availableSpots = slotService?.service.availableSpots ?? 1;
  return availableSpots <= MAX_PARTICIPANTS_PER_BOOKING
    ? 'bookings.form.collectParticipant.addParticipant.noSpotsLeftTooltip'
    : 'bookings.form.collectParticipant.addParticipant.maxReachedTooltip';
};

export const PARTICIPANT_BADGE_KEY =
  'bookings.form.collectParticipant.section.badge';

/** "Participant N" label. */
export const getParticipantLabel = (
  t: (key: string, options?: Record<string, unknown>) => string,
  number: number,
): string => t(PARTICIPANT_BADGE_KEY, { number });

/**
 * "Participant 2 - Dana Levi" — the heading shown above a participant's form and above
 * their block in Booking Details. Falls back to "Participant 2" until a name is typed.
 * One helper so the two places cannot drift apart.
 */
export const getParticipantHeading = (
  t: (key: string, options?: Record<string, unknown>) => string,
  number: number,
  name?: string,
): string => [getParticipantLabel(t, number), name].filter(Boolean).join(' - ');
